/**
 * What counts as an acceptable password, in one place.
 *
 * The same rules have to hold in three places: the change-password screen while
 * you type, the admin's create/reset form, and the API that actually stores the
 * hash. Three copies of "at least 12 characters" is how a screen ends up
 * accepting something the server then rejects, or worse, the other way round.
 *
 * The rules follow length-and-blocklist rather than the familiar
 * upper/lower/digit/symbol composition test. Composition rules reliably produce
 * `Password1!` — they measure whether someone can satisfy a regex, not whether
 * the password is hard to guess. Length, a blocklist of the obvious local
 * guesses, and a ban on your own name and address do measure that.
 *
 * Deliberately not here: a strength meter's score. A number between 0 and 4
 * invites arguing with the bar instead of choosing a phrase.
 */
/** Twelve, not eight.
 *
 * These accounts reach every customer's address and phone number, there are a
 * handful of them, and nobody types it more than once a day — so the usual
 * argument for a shorter minimum does not apply.
 */
export const MIN_PASSWORD_LENGTH = 12;
/**
 * Over-long input is refused rather than truncated. bcrypt ignores everything
 * past 72 bytes, and a password silently cut in half is a password that is not
 * the one the person thinks they set.
 */
export const MAX_PASSWORD_LENGTH = 72;
/**
 * The guesses somebody would actually try against this business, not a leaked
 * top-10,000 list. A big list belongs in a file and a lookup; this is the set
 * that is specific to GoSpeedNet and Kisii, which no generic list contains.
 */
const OBVIOUS = [
    'password',
    'passw0rd',
    'letmein',
    'welcome',
    'qwerty',
    'iloveyou',
    'admin',
    'administrator',
    'dispatch',
    'gospeednet',
    'speednet',
    'kisii',
    'kenya',
    'nyanza',
    'egesa',
    'internet',
    'fibre',
    'fiber',
    'router',
    'technician',
];
/** Strips anything that is not a letter or digit, so `K1s!i` still reads as `k1si`. */
const squash = (value) => value.toLowerCase().replace(/[^a-z0-9]/g, '');
/** `a1b2c3` → `abc`, so `passw0rd` and `p4ssword` collapse onto `password`. */
const deleet = (value) => value
    .toLowerCase()
    .replace(/[@4]/g, 'a')
    .replace(/[3€]/g, 'e')
    .replace(/[1!|]/g, 'i')
    .replace(/0/g, 'o')
    .replace(/[5$]/g, 's')
    .replace(/7/g, 't');
/** One character repeated — `aaaaaaaaaaaa` clears the length rule and nothing else. */
const isRepetitive = (value) => new Set(value.toLowerCase()).size <= 3;
/** A run along the keyboard or the number line, forwards or backwards. */
const hasLongRun = (value) => {
    const sequences = ['abcdefghijklmnopqrstuvwxyz', '01234567890', 'qwertyuiop', 'asdfghjkl'];
    const lower = value.toLowerCase();
    for (const sequence of sequences) {
        const backwards = [...sequence].reverse().join('');
        for (const haystack of [sequence, backwards]) {
            for (let start = 0; start + 6 <= haystack.length; start += 1) {
                if (lower.includes(haystack.slice(start, start + 6)))
                    return true;
            }
        }
    }
    return false;
};
/**
 * Every reason this password should be refused, in the order worth fixing.
 *
 * Returns all of them rather than the first: someone told "too short", who then
 * lengthens it and is told "that is your own name", has been made to guess
 * twice at a rule nobody stated.
 */
export function passwordProblems(password, context = {}) {
    const problems = [];
    if (password.length < MIN_PASSWORD_LENGTH) {
        problems.push(`Use at least ${MIN_PASSWORD_LENGTH} characters.`);
    }
    if (password.length > MAX_PASSWORD_LENGTH) {
        problems.push(`Keep it under ${MAX_PASSWORD_LENGTH} characters.`);
    }
    if (password.trim() !== password) {
        // A leading or trailing space survives a paste and disappears when typed,
        // which produces a password that works once and never again.
        problems.push('Remove the space at the start or end.');
    }
    const flat = squash(password);
    const plain = deleet(password);
    if (password.length >= MIN_PASSWORD_LENGTH && isRepetitive(password)) {
        problems.push('That is the same few characters repeated. Use a phrase instead.');
    }
    if (hasLongRun(password)) {
        problems.push('That runs straight along the keyboard. Use a phrase instead.');
    }
    for (const word of OBVIOUS) {
        if (plain.includes(word) || flat.includes(word)) {
            problems.push(`Do not build it around "${word}" — it is the first thing anyone would try.`);
            break;
        }
    }
    // Your own name and address are public to everyone who has ever received an
    // email from you, which makes them the cheapest guess available.
    //
    // Each part on its own as well as the whole: "Peter Otieno" checked as one
    // string misses `otieno-rules-the-day`, and the surname alone is exactly what
    // somebody would try. Parts under four characters are skipped, or a person
    // called Jo could not use "join" or "enjoy".
    const localPart = context.email?.split('@')[0] ?? '';
    for (const [label, value] of [
        ['your email address', localPart],
        ['your name', context.name ?? ''],
    ]) {
        const needles = [value, ...value.split(/[\s._@-]+/)]
            .map(squash)
            .filter((needle) => needle.length >= 4);
        if (needles.some((needle) => flat.includes(needle))) {
            problems.push(`Do not use ${label} in your password.`);
        }
    }
    if (context.currentPassword && password === context.currentPassword) {
        problems.push('The new password has to be different from the old one.');
    }
    return problems;
}
/** Convenience for the places that only need a yes or no. */
export const isAcceptablePassword = (password, context = {}) => passwordProblems(password, context).length === 0;
/**
 * The words a generated handover password is built from.
 *
 * Short, unambiguous, and typeable by someone reading them off a phone screen
 * in the sun. No word here can be confused with another when spoken aloud,
 * because in practice this password gets read down a phone line.
 */
const WORDS = [
    'anchor', 'basket', 'candle', 'copper', 'dinner', 'engine', 'falcon', 'garden',
    'hammer', 'island', 'jacket', 'kettle', 'ladder', 'magnet', 'napkin', 'orbit',
    'pencil', 'quartz', 'rocket', 'saddle', 'timber', 'umbrella', 'valley', 'walnut',
    'yellow', 'zebra', 'bridge', 'cactus', 'dragon', 'ember', 'forest', 'glacier',
];
/**
 * A handover password an admin can read out loud.
 *
 * Three words and a number is roughly 20 bits from the words plus 10 from the
 * digits — weak by the standards of a password you keep, and irrelevant here,
 * because this one is single-use: the account cannot be used until the person
 * replaces it, and the rate limiter allows five guesses per quarter hour.
 *
 * `randomInt` is passed in so this stays a pure function and the tests can
 * pin an exact output. Callers hand it a CSPRNG — `crypto.getRandomValues` in
 * the browser, `crypto.randomInt` on the server. Never `Math.random()`.
 */
export function generateHandoverPassword(randomInt) {
    const words = Array.from({ length: 3 }, () => WORDS[randomInt(WORDS.length)]);
    // Capitalised and hyphenated so it is unambiguous when written on paper, and
    // padded to four digits so it never comes out under the length minimum.
    const number = String(randomInt(9000) + 1000);
    return `${words.map((w) => w[0].toUpperCase() + w.slice(1)).join('-')}-${number}`;
}
//# sourceMappingURL=passwords.js.map